// ADV-MKT-WATCHLIST-ADD-ACTION-001 — one-click "+ Watchlist" action for board rows.
// Watchlist ONLY — never a trade/order/broker action. A membership context (provided by
// the board) lets every row know if its symbol is already on the Active Watchlist without
// N fetches, and refreshes when the watchlist changes anywhere.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Plus, Check, Loader2, AlertCircle } from 'lucide-react';
import {
  addToWatchlist, fetchWatchlistSymbols, dispatchWatchlistChanged,
  normalizeSymbol, WATCHLIST_CHANGED_EVENT,
} from '../lib/watchlistActions';

interface Membership { has: (s: string) => boolean; markAdded: (s: string) => void; ready: boolean; }
const WatchlistMembershipContext = createContext<Membership>({ has: () => false, markAdded: () => {}, ready: false });

export function WatchlistMembershipProvider({ children }: { children: React.ReactNode }) {
  const [set, setSet] = useState<Set<string>>(new Set());
  const [ready, setReady] = useState(false);
  const load = useCallback(async () => {
    const syms = await fetchWatchlistSymbols();
    setSet(new Set(syms.map(normalizeSymbol)));
    setReady(true);
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const h = () => { void load(); };
    window.addEventListener(WATCHLIST_CHANGED_EVENT, h);
    return () => window.removeEventListener(WATCHLIST_CHANGED_EVENT, h);
  }, [load]);
  const has = useCallback((s: string) => set.has(normalizeSymbol(s)), [set]);
  const markAdded = useCallback((s: string) => setSet((prev) => new Set(prev).add(normalizeSymbol(s))), []);
  return (
    <WatchlistMembershipContext.Provider value={{ has, markAdded, ready }}>
      {children}
    </WatchlistMembershipContext.Provider>
  );
}

type BtnState = 'idle' | 'adding' | 'added' | 'error';

// variant 'chip' (default) = compact board-row pill; 'toolbar' = sized to sit in the
// chart/detail technical toolbar row alongside the view-toggle tabs (ADV-MKT-CHART-VIEW-
// WATCHLIST-001). Same logic, backend, states, testid and watchlist-changed event — only
// the wrapper sizing differs. Never an order/trade/broker action.
export function WatchlistAddButton({ symbol, variant = 'chip' }: { symbol: string; variant?: 'chip' | 'toolbar' }) {
  const { has, markAdded } = useContext(WatchlistMembershipContext);
  const [state, setState] = useState<BtnState>('idle');
  const inList = has(symbol) || state === 'added';

  const onClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (inList || state === 'adding') return;            // dedupe: never double-add
    setState('adding');
    const res = await addToWatchlist(symbol);
    if (res.ok) {
      setState('added');
      markAdded(symbol);
      dispatchWatchlistChanged(symbol);                  // Active Watchlist panel refreshes
    } else {
      setState('error');
    }
  };

  // ADV-MKT-WATCHLIST-BUTTON-SIZE-001 — compact pill sized to sit alongside the row
  // chips (not dominate them): ~80px wide / ~20px tall, 10px text, lighter green.
  // Still clearly visible & clickable; min-width keeps all states the same size so
  // the row never reflows between idle/adding/added/error.
  // Toolbar variant mirrors the adjacent view-toggle tab sizing (px-4 py-2, italic) with a
  // fixed min-width so the button never reflows the toolbar between its four states.
  const chipBase = 'inline-flex items-center justify-center gap-1 min-w-[80px] px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide border transition-colors';
  const toolbarBase = 'inline-flex items-center justify-center gap-2 min-w-[136px] px-4 py-2 rounded-lg text-[10px] font-black uppercase italic tracking-wide border transition-all';
  const base = variant === 'toolbar' ? toolbarBase : chipBase;
  const isz = variant === 'toolbar' ? 13 : 11;
  if (inList) {
    return (
      <span data-testid={`mp-watchlist-add-${symbol}`} data-state="added" data-variant={variant} title="On your Active Watchlist (watch only — not a trade)"
            className={`${base} border-emerald-500/40 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 cursor-default`}>
        <Check size={isz} /> In Watchlist
      </span>
    );
  }
  if (state === 'adding') {
    return (
      <span data-testid={`mp-watchlist-add-${symbol}`} data-state="adding" data-variant={variant}
            className={`${base} border-emerald-500/25 text-[var(--muted-text)] bg-[var(--input-bg)]`}>
        <Loader2 size={isz} className="animate-spin" /> Adding…
      </span>
    );
  }
  return (
    <button type="button" data-testid={`mp-watchlist-add-${symbol}`} data-symbol={symbol}
            data-state={state === 'error' ? 'error' : 'idle'} data-variant={variant} onClick={onClick}
            title={state === 'error' ? 'Add failed — click to retry (watch only, not a trade)' : `Add ${symbol} to your Active Watchlist (watch only — not a trade)`}
            aria-label={`Add ${symbol} to watchlist`}
            className={`${base} cursor-pointer ${state === 'error'
              ? 'border-rose-500/40 text-rose-600 dark:text-rose-400 bg-rose-500/10 hover:bg-rose-500/15 hover:border-rose-500/60'
              : 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400 bg-emerald-500/5 hover:bg-emerald-500/15 hover:border-emerald-500/70'}`}>
      {state === 'error' ? <><AlertCircle size={isz} /> Error — retry</> : <><Plus size={isz} /> Watchlist</>}
    </button>
  );
}
